import { usePanelResize } from "@repo/ui/hooks/usePanelResize";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useViewportWidth } from "../../hooks/useViewportWidth.js";
import { useResearchAgentProps } from "./researchAgentContext.js";
import { ResearchAgentExpandedModal } from "./researchAgentExpandedModal.js";
import {
  RESEARCH_AGENT_PANEL_MAX_WIDTH_PX,
  RESEARCH_AGENT_PANEL_WIDTH_PX,
  ResearchAgentSidePanel,
} from "./researchAgentSidePanel.js";

/**
 * Never let the panel crush the page: whatever the user drags it to, leave
 * this much room for the nav and a usable main column.
 */
const RESERVED_MAIN_WIDTH_PX = 640;

/**
 * Global research-agent chrome: the docked, resizable right-hand panel beside
 * the white body card, plus the expanded modal. Renders nothing when the
 * research agent isn't available on the current route.
 */
export const ResearchAgentShell: FC = () => {
  const researchAgent = useResearchAgentProps();
  const isPanelVisible = researchAgent?.isPanelVisible ?? false;
  const [panelWidth, setPanelWidth] = useState(RESEARCH_AGENT_PANEL_WIDTH_PX);

  // Clamp against the viewport here rather than in CSS: a `max-width` the drag
  // doesn't know about lets the stored width run past what's rendered, so the
  // handle detaches from the pointer and has to be dragged back across the
  // difference before the panel moves again.
  const viewportWidth = useViewportWidth();
  const maxPanelWidth = Math.max(
    RESEARCH_AGENT_PANEL_WIDTH_PX,
    Math.min(
      RESEARCH_AGENT_PANEL_MAX_WIDTH_PX,
      viewportWidth - RESERVED_MAIN_WIDTH_PX,
    ),
  );

  // Mount the panel lazily, then keep it mounted: unmounting on collapse would
  // empty the column before it finished animating shut. Adjusting state during
  // render (rather than in an effect) mounts it in the same commit that starts
  // the animation, so the first open doesn't flash an empty column.
  const [didExpand, setDidExpand] = useState(isPanelVisible);
  if (isPanelVisible && !didExpand) {
    setDidExpand(true);
  }

  const { separatorProps, isResizing, resizeOverlay } = usePanelResize({
    size: panelWidth,
    minSize: RESEARCH_AGENT_PANEL_WIDTH_PX,
    maxSize: maxPanelWidth,
    onSizeChange: setPanelWidth,
    edge: "left",
    label: "Resize chat panel",
  });

  // Shrinking the window can leave the stored width above the new cap.
  const renderedPanelWidth = Math.min(panelWidth, maxPanelWidth);

  if (!researchAgent) {
    return null;
  }

  return (
    <>
      <div
        // The collapsed panel is only visually hidden; inert also removes it
        // from the tab order and accessibility tree. Set via ref because
        // React 18 does not support the `inert` prop.
        ref={(element) => {
          if (element) {
            element.inert = !isPanelVisible;
          }
        }}
        style={{ width: isPanelVisible ? renderedPanelWidth : 0 }}
        className={twMerge(
          "relative flex min-h-0 shrink-0 self-stretch transition-[width,opacity] duration-300 ease-in-out motion-reduce:transition-none",
          isPanelVisible ? "opacity-100" : "opacity-0",
          isResizing && "transition-none",
        )}
      >
        {/* The content keeps its full width while the column animates, so the
            panel slides past the column's right edge instead of reflowing its
            text on every frame; the app layout clips it at the window edge. It
            transitions on the same curve so that a width change
            (double-clicking the handle to reset) still moves the content and
            the column as one. */}
        <div
          style={{ width: renderedPanelWidth }}
          className={twMerge(
            "h-full shrink-0 transition-[width] duration-300 ease-in-out motion-reduce:transition-none",
            isResizing && "transition-none",
          )}
        >
          {didExpand ? (
            <ResearchAgentSidePanel researchAgent={researchAgent} />
          ) : null}
        </div>

        {isPanelVisible ? (
          <div
            {...separatorProps}
            className="group absolute inset-y-3 left-0 z-20 flex w-2 cursor-col-resize items-stretch justify-center outline-none"
            onDoubleClick={(event) => {
              event.preventDefault();
              setPanelWidth(RESEARCH_AGENT_PANEL_WIDTH_PX);
            }}
          >
            <div
              aria-hidden
              className="w-0.5 rounded-full bg-transparent transition-colors group-hover:bg-border-strong group-focus-visible:bg-border-strong"
            />
          </div>
        ) : null}
      </div>

      {resizeOverlay}

      <ResearchAgentExpandedModal researchAgent={researchAgent} />
    </>
  );
};
